"use client";

import { useQuery } from "@tanstack/react-query";
import { searchRecipients, type UserSummary } from "../api/messages";
import { useDebounced } from "./useDebounced";

const EMPTY: UserSummary[] = [];

/**
 * Recipient lookup for the DM picker.
 *
 * `GET /users?messageableBy=me` IS the DM policy (§1.4 makes the scope
 * mandatory), so the picker can no longer offer someone the send would then
 * refuse — which is exactly why the legacy code had to avoid the anonymous,
 * unscoped `/theta/users/search`. The hand-rolled debounce-and-cancel is a
 * cached, debounced query now; react-query drops the answer to a term the user
 * has already typed past.
 *
 * `truncated` IS READ, and that is the whole difference from a bare
 * `data.items`. This does not page — a dropdown under a search box is a "type
 * more" affordance — so `hasMore` is the route telling the caller that the
 * answer it just gave is incomplete. `lib/api/messages.ts` asks for the
 * route's maximum precisely so that flag means "narrow the term", not "we
 * asked for twenty"; the picker then has something honest to say instead of
 * showing a full-looking list the person they want is simply absent from.
 */
export function useRecipientSearch(term: string): {
  results: UserSummary[];
  truncated: boolean;
} {
  const trimmed = useDebounced(term.trim(), 200);
  const { data } = useQuery({
    queryKey: ["v1", "recipients", trimmed],
    queryFn: () => searchRecipients(trimmed),
    enabled: trimmed.length > 0,
    staleTime: 60 * 1000,
  });
  if (trimmed.length === 0 || !data) return { results: EMPTY, truncated: false };
  return { results: data.items, truncated: data.hasMore };
}
